import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Search,
  Filter,
  Download,
  RefreshCcw,
  Check,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Table Container
interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  children: React.ReactNode;
}

export function Table({ className, children, ...props }: TableProps) {
  return (
    <div className="relative w-full overflow-x-auto overflow-y-visible">
      <table
        className={cn(
          'w-full caption-bottom text-sm',
          className
        )}
        {...props}
      >
        {children}
      </table>
    </div>
  );
}

// Table Header
interface TableHeaderProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  children: React.ReactNode;
}

export function TableHeader({ className, children, ...props }: TableHeaderProps) {
  return (
    <thead className={cn('[&_tr]:border-b border-slate-800', className)} {...props}>
      {children}
    </thead>
  );
}

// Table Body
interface TableBodyProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  children: React.ReactNode;
}

export function TableBody({ className, children, ...props }: TableBodyProps) {
  return (
    <tbody className={cn('[&_tr:last-child]:border-0', className)} {...props}>
      {children}
    </tbody>
  );
}

// Table Row
interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  children: React.ReactNode;
  isSelected?: boolean;
  isClickable?: boolean;
}

export function TableRow({ className, children, isSelected, isClickable, ...props }: TableRowProps) {
  return (
    <tr
      className={cn(
        'border-b border-slate-800/50 transition-colors',
        isClickable && 'cursor-pointer hover:bg-slate-800/30',
        isSelected && 'bg-violet-500/10',
        className
      )}
      {...props}
    >
      {children}
    </tr>
  );
}

// Table Head Cell
interface TableHeadProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  children?: React.ReactNode;
  sortable?: boolean;
  sorted?: 'asc' | 'desc' | false;
  onSort?: () => void;
}

export function TableHead({ className, children, sortable, sorted, onSort, ...props }: TableHeadProps) {
  return (
    <th
      className={cn(
        'h-12 px-4 text-left align-middle font-semibold text-slate-400 [&:has([role=checkbox])]:pr-0',
        sortable && 'cursor-pointer select-none hover:text-slate-200 transition-colors',
        className
      )}
      onClick={sortable ? onSort : undefined}
      {...props}
    >
      <div className="flex items-center gap-2">
        {children}
        {sortable && (
          <span className="flex-shrink-0">
            {sorted === 'asc' && <ArrowUp className="w-4 h-4" />}
            {sorted === 'desc' && <ArrowDown className="w-4 h-4" />}
            {!sorted && <ArrowUpDown className="w-4 h-4 opacity-50" />}
          </span>
        )}
      </div>
    </th>
  );
}

// Table Cell
interface TableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  children?: React.ReactNode;
}

export function TableCell({ className, children, ...props }: TableCellProps) {
  return (
    <td
      className={cn('p-4 align-middle [&:has([role=checkbox])]:pr-0', className)}
      {...props}
    >
      {children}
    </td>
  );
}

// Table Footer
interface TableFooterProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  children: React.ReactNode;
}

export function TableFooter({ className, children, ...props }: TableFooterProps) {
  return (
    <tfoot
      className={cn('border-t border-slate-800 bg-slate-900/50', className)}
      {...props}
    >
      {children}
    </tfoot>
  );
}

// Table Caption
interface TableCaptionProps extends React.HTMLAttributes<HTMLTableCaptionElement> {
  children: React.ReactNode;
}

export function TableCaption({ className, children, ...props }: TableCaptionProps) {
  return (
    <caption className={cn('mt-4 text-sm text-slate-500', className)} {...props}>
      {children}
    </caption>
  );
}

// Pagination Component
interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  showItemsPerPage?: boolean;
  className?: string;
}

export function Pagination({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  showItemsPerPage = true,
  className,
}: PaginationProps) {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <div className={cn('flex flex-col sm:flex-row items-center justify-between gap-4 px-2 py-4', className)}>
      <div className="flex items-center gap-4 text-sm text-slate-400">
        <span>
          Showing {startItem} to {endItem} of {totalItems} results
        </span>
        {showItemsPerPage && onItemsPerPageChange && (
          <div className="flex items-center gap-2">
            <span>Show</span>
            <select
              value={itemsPerPage}
              onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
              className="px-2 py-1 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span>per page</span>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          className="p-2 rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="First page"
        >
          <ChevronsLeft className="w-4 h-4" />
        </button>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="p-2 rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Previous page"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-1">
          {getPageNumbers().map((page, index) => (
            <React.Fragment key={index}>
              {page === '...' ? (
                <span className="px-2 text-slate-500">...</span>
              ) : (
                <button
                  onClick={() => onPageChange(page as number)}
                  className={cn(
                    'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                    currentPage === page
                      ? 'bg-violet-600 text-white'
                      : 'hover:bg-slate-800 text-slate-400'
                  )}
                >
                  {page}
                </button>
              )}
            </React.Fragment>
          ))}
        </div>

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="p-2 rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Next page"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          className="p-2 rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Last page"
        >
          <ChevronsRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// Table Toolbar
interface TableToolbarProps {
  children?: React.ReactNode;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  onRefresh?: () => void;
  onExport?: () => void;
  isLoading?: boolean;
  className?: string;
}

export function TableToolbar({
  children,
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search...',
  onRefresh,
  onExport,
  isLoading,
  className,
}: TableToolbarProps) {
  return (
    <div className={cn('flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4', className)}>
      <div className="flex items-center gap-3 flex-1">
        {onSearchChange && (
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-slate-900/50 border border-slate-800 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500/50 transition-all"
            />
          </div>
        )}
        {children}
      </div>

      <div className="flex items-center gap-2">
        {onRefresh && (
          <button
            onClick={onRefresh}
            disabled={isLoading}
            className="p-2.5 rounded-xl bg-slate-900/50 border border-slate-800 hover:bg-slate-800 disabled:opacity-50 transition-colors"
            title="Refresh"
          >
            <RefreshCcw className={cn('w-4 h-4', isLoading && 'animate-spin')} />
          </button>
        )}
        {onExport && (
          <button
            onClick={onExport}
            className="p-2.5 rounded-xl bg-slate-900/50 border border-slate-800 hover:bg-slate-800 transition-colors"
            title="Export"
          >
            <Download className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

// Empty State
interface TableEmptyStateProps {
  title?: string;
  description?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
}

export function TableEmptyState({
  title = 'No results found',
  description = 'Try adjusting your search or filter to find what you are looking for.',
  icon,
  action,
}: TableEmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      {icon && <div className="mb-4 text-slate-600">{icon}</div>}
      <h3 className="text-lg font-semibold text-slate-300 mb-2">{title}</h3>
      <p className="text-slate-500 max-w-md mb-4">{description}</p>
      {action}
    </div>
  );
}

// Loading Skeleton
interface TableSkeletonProps {
  columns: number;
  rows?: number;
}

export function TableSkeleton({ columns, rows = 5 }: TableSkeletonProps) {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex gap-4 items-center py-4 px-4 animate-pulse">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <div
              key={colIndex}
              className="h-4 bg-slate-800 rounded flex-1"
              style={{ maxWidth: colIndex === 0 ? '60%' : '100%' }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

// Checkbox for selection
interface TableCheckboxProps {
  checked?: boolean;
  indeterminate?: boolean;
  onChange?: (checked: boolean) => void;
}

export function TableCheckbox({ checked, indeterminate, onChange }: TableCheckboxProps) {
  const ref = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate ?? false;
    }
  }, [indeterminate]);

  return (
    <div className="relative inline-flex items-center justify-center">
      <input
        ref={ref}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange?.(e.target.checked)}
        className="peer sr-only"
      />
      <div
        onClick={() => onChange?.(!checked)}
        className={cn(
          'w-5 h-5 rounded border-2 cursor-pointer transition-all duration-200',
          'flex items-center justify-center',
          checked || indeterminate
            ? 'bg-violet-600 border-violet-600'
            : 'bg-transparent border-slate-600 hover:border-violet-500'
        )}
      >
        {checked && <Check className="w-3 h-3 text-white" />}
        {indeterminate && !checked && (
          <div className="w-2.5 h-0.5 bg-white rounded" />
        )}
      </div>
    </div>
  );
}

// Bulk Actions Bar
interface BulkActionsBarProps {
  selectedCount: number;
  onClearSelection: () => void;
  children: React.ReactNode;
}

export function BulkActionsBar({ selectedCount, onClearSelection, children }: BulkActionsBarProps) {
  return (
    <AnimatePresence>
      {selectedCount > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50"
        >
          <div className="flex items-center gap-4 px-6 py-3 rounded-2xl bg-slate-900 border border-slate-700 shadow-xl shadow-black/20">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-violet-600 flex items-center justify-center">
                <span className="text-sm font-bold">{selectedCount}</span>
              </div>
              <span className="text-sm text-slate-400">selected</span>
            </div>
            <div className="w-px h-8 bg-slate-700" />
            <div className="flex items-center gap-2">
              {children}
            </div>
            <div className="w-px h-8 bg-slate-700" />
            <button
              onClick={onClearSelection}
              className="text-sm text-slate-400 hover:text-white transition-colors"
            >
              Clear
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Filter Dropdown
interface FilterOption {
  label: string;
  value: string;
}

interface FilterDropdownProps {
  label: string;
  options: FilterOption[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

export function FilterDropdown({ label, options, value, onChange, className }: FilterDropdownProps) {
  return (
    <div className={cn('relative', className)}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="appearance-none px-4 py-2.5 pr-10 rounded-xl bg-slate-900/50 border border-slate-800 text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50 cursor-pointer"
      >
        <option value="">{label}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 rotate-90 pointer-events-none" />
    </div>
  );
}

// Date Range Filter
interface DateRangeFilterProps {
  startDate?: string;
  endDate?: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  className?: string;
}

export function DateRangeFilter({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  className,
}: DateRangeFilterProps) {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <input
        type="date"
        value={startDate || ''}
        onChange={(e) => onStartDateChange(e.target.value)}
        className="px-3 py-2 rounded-xl bg-slate-900/50 border border-slate-800 text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
      />
      <span className="text-slate-500">to</span>
      <input
        type="date"
        value={endDate || ''}
        onChange={(e) => onEndDateChange(e.target.value)}
        className="px-3 py-2 rounded-xl bg-slate-900/50 border border-slate-800 text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
      />
    </div>
  );
}

// Filter Panel Component
interface FilterPanelProps {
  children: React.ReactNode;
  className?: string;
  isOpen?: boolean;
  onClose?: () => void;
}

export function FilterPanel({ children, className, isOpen = true, onClose }: FilterPanelProps) {
  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      className={cn(
        'bg-slate-900/50 border border-slate-800 rounded-xl p-4 mb-4',
        className
      )}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-violet-400" />
          <span className="text-sm font-medium">Filters</span>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-white transition-colors"
          >
            <span className="sr-only">Close filters</span>
            ×
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-4">
        {children}
      </div>
    </motion.div>
  );
}
